import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Megaphone, AlertTriangle, AlertOctagon } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface Announcement {
  id: number;
  title: string;
  body: string;
  severity: 'info' | 'warning' | 'critical';
  pinned: boolean;
}

export function AnnouncementBanner() {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['comm-announcements'],
    queryFn: async () => {
      const r = await api.get('/announcements');
      return (r.data.announcements ?? []) as Announcement[];
    },
    refetchInterval: 60_000,
    // Don't error out the whole app if the user isn't authenticated yet.
    retry: false,
  });

  const dismiss = useMutation({
    mutationFn: async (id: number) => api.post(`/announcements/${id}/dismiss`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comm-announcements'] }),
  });

  const items = query.data ?? [];
  if (!items.length) return null;

  return (
    <div className="flex flex-col gap-1 px-4 pt-2">
      {items.map((a) => (
        <div
          key={a.id}
          className={cn(
            'flex items-start gap-3 rounded-lg px-3 py-2 border text-sm',
            a.severity === 'critical' && 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-900 dark:text-red-100',
            a.severity === 'warning' && 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-100',
            a.severity === 'info' && 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-900 dark:text-blue-100',
          )}
        >
          <div className="mt-0.5 shrink-0">
            {a.severity === 'critical' && <AlertOctagon size={16} />}
            {a.severity === 'warning' && <AlertTriangle size={16} />}
            {a.severity === 'info' && <Megaphone size={16} />}
          </div>
          <div className="flex-1">
            <div className="font-semibold">{a.title}</div>
            <div className="opacity-90">{a.body}</div>
          </div>
          <button
            onClick={() => dismiss.mutate(a.id)}
            className="opacity-60 hover:opacity-100 shrink-0"
            title="Odrzuć"
          >
            <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
